/**
 * GuidesScreen — port of garageiq-web/app/(main)/guides/GuidesClient.jsx.
 *
 * Filters the global guide list to ones that match the active vehicle
 * (from useGarage), with secondary filters: tab type (maintenance / repair /
 * road-trip-inspection), category (engine / suspension etc), and difficulty,
 * plus a search box.
 *
 * Differences vs. web:
 *  - Single-column FlatList instead of responsive 2/3-col grid
 *  - GarageTabs (horizontal pill scroller across saved vehicles) is replaced
 *    by a single chip showing the active vehicle + a "Change" link to
 *    VehicleSelectorScreen. Multi-vehicle GarageTabs is a v2 task.
 *  - Sticky filter header → header rendered above the FlatList; the user
 *    scrolls, but it does NOT stick (no stickyHeaderIndices on FlatList yet
 *    so we just keep it scroll-with-content for now).
 */
import React, { useMemo, useState } from 'react';
import { View, Text, TextInput, Pressable, FlatList } from 'react-native';
import { useNavigation } from '@react-navigation/native';

import { guides as allGuides } from '../lib/guideData';
import { formatVehicle } from '../lib/vehicleData';
import { guideMatchesVehicle } from '../lib/guideMatching';
import {
  getGuideTabType, getCategoryList, TAB_TYPE_DISPLAY,
  getShortInterval, getIntervalLabel,
} from '../lib/guideTabType';
import { useGarage } from '../lib/useGarage';
import { IconWrench } from '../components/Icons';

const DIFF_BADGE = {
  beginner:     { wrap: 'bg-green-500/10 border-green-500/20',   text: 'text-green-400' },
  intermediate: { wrap: 'bg-yellow-500/10 border-yellow-500/20', text: 'text-yellow-400' },
  advanced:     { wrap: 'bg-red-500/10 border-red-500/20',       text: 'text-red-400' },
};

function DifficultyBadge({ level = 'beginner' }) {
  const s = DIFF_BADGE[level] || DIFF_BADGE.beginner;
  return (
    <View className={`px-2.5 py-1 rounded-full border ${s.wrap}`}>
      <Text className={`text-[11px] font-bold capitalize ${s.text}`}>{level}</Text>
    </View>
  );
}

function CategoryBadge({ guide }) {
  const tabType = getGuideTabType(guide);
  const display = TAB_TYPE_DISPLAY[tabType] || TAB_TYPE_DISPLAY.repair;
  // The web version uses a Tailwind class (display.badge). NativeWind needs
  // those classes to appear literally somewhere; switching by tab type:
  const wrap =
    tabType === 'maintenance' ? 'bg-blue-500/10 border-blue-500/20' :
    tabType === 'road-trip-inspection' ? 'bg-orange-500/10 border-orange-500/20' :
    'bg-purple-500/10 border-purple-500/20';
  const textColor =
    tabType === 'maintenance' ? 'text-blue-400' :
    tabType === 'road-trip-inspection' ? 'text-orange-400' :
    'text-purple-400';
  return (
    <View className={`px-2 py-0.5 rounded border ${wrap}`}>
      <Text className={`text-[11px] font-bold capitalize ${textColor}`}>{display.label}</Text>
    </View>
  );
}

function FilterPill({ label, active, onPress, variant = 'default' }) {
  const wrap = active
    ? variant === 'primary'
      ? 'bg-sky border-sky'
      : 'bg-white/15 border-white/20'
    : 'bg-white/5 border-transparent';
  const textColor = active ? 'text-white' : 'text-white/40';
  return (
    <Pressable onPress={onPress} className={`px-3 py-1.5 rounded-full border mr-2 mb-2 ${wrap} active:opacity-80`}>
      <Text className={`text-[11px] font-bold capitalize ${textColor}`}>{label}</Text>
    </Pressable>
  );
}

function GuideCard({ guide, isMatch, onPress }) {
  const tabType = getGuideTabType(guide);
  const shortInterval = getShortInterval(guide.interval);
  const intervalLabel = getIntervalLabel(tabType);
  const labelColor =
    tabType === 'maintenance' ? 'text-blue-400/80' :
    tabType === 'road-trip-inspection' ? 'text-orange-400/80' :
    'text-purple-400/80';

  return (
    <Pressable
      onPress={onPress}
      className={`bg-charcoal/50 border rounded-2xl p-5 mb-3 active:opacity-80 ${isMatch ? 'border-sky/40' : 'border-white/10'}`}
    >
      {isMatch && (
        <View className="absolute -top-2 right-3 bg-sky px-2.5 py-0.5 rounded-full">
          <Text className="text-white text-[10px] font-black">YOUR CAR</Text>
        </View>
      )}
      <View className="flex-row items-center justify-between mb-2">
        <View className="flex-row items-center gap-2">
          <DifficultyBadge level={guide.difficulty || 'beginner'} />
          <CategoryBadge guide={guide} />
        </View>
        <Text className="text-xs text-white/30 font-medium">{guide.duration || '30 min'}</Text>
      </View>

      <Text className="text-base font-black text-white mb-1">{guide.title}</Text>
      <Text className="text-xs text-white/30 mb-2 font-medium">
        {guide.vehicle.year} {guide.vehicle.make} {guide.vehicle.model} {guide.vehicle.trim}
      </Text>

      {shortInterval && (
        <View className="flex-row items-center mb-2">
          <Text className={`text-[11px] font-bold uppercase tracking-wider mr-1.5 ${labelColor}`}>
            {intervalLabel}:
          </Text>
          <Text className="text-[11px] text-white/60 flex-1" numberOfLines={1}>{shortInterval}</Text>
        </View>
      )}

      <Text className="text-sm text-white/50 leading-5" numberOfLines={3}>
        {guide.description || 'Detailed maintenance and repair guide'}
      </Text>

      <View className="flex-row items-center justify-between mt-4 pt-3 border-t border-white/10">
        <Text className="text-xs text-white/30">
          <Text className="font-bold text-white/60">{(guide.steps || []).length}</Text> steps · {' '}
          <Text className="font-bold text-white/60">{(guide.parts || []).length}</Text> parts
        </Text>
        <Text className="text-sm font-bold text-sky">Start →</Text>
      </View>
    </Pressable>
  );
}

export default function GuidesScreen() {
  const navigation = useNavigation();
  const { activeVehicle, mounted } = useGarage();
  const [filterDifficulty, setFilterDifficulty] = useState('all');
  const [filterTabType, setFilterTabType] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const myGuides = useMemo(
    () => activeVehicle ? allGuides.filter(g => guideMatchesVehicle(g, activeVehicle)) : [],
    [activeVehicle],
  );

  const filtered = useMemo(() => {
    let list = myGuides;
    if (filterTabType !== 'all') list = list.filter(g => getGuideTabType(g) === filterTabType);
    if (filterCategory !== 'all') list = list.filter(g => g.category === filterCategory);
    if (filterDifficulty !== 'all') list = list.filter(g => g.difficulty === filterDifficulty);
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter(g =>
        (g.title || '').toLowerCase().includes(q) ||
        (g.description || '').toLowerCase().includes(q),
      );
    }
    return list;
  }, [myGuides, filterTabType, filterCategory, filterDifficulty, searchQuery]);

  const categoryList = useMemo(() => getCategoryList(myGuides), [myGuides]);

  if (!mounted) {
    return <View className="flex-1 bg-navy" />;
  }

  // Header rendered as FlatList ListHeaderComponent so it scrolls with content.
  const Header = (
    <View className="px-5 pt-4 pb-2">
      {/* Active vehicle chip */}
      <Pressable
        onPress={() => navigation.navigate('VehicleSelector')}
        className="bg-charcoal/50 border border-white/10 rounded-xl px-4 py-3 mb-4 flex-row items-center justify-between active:opacity-80"
      >
        <View className="flex-1">
          <Text className="text-[10px] font-bold text-white/40 uppercase tracking-wider">Active Vehicle</Text>
          <Text className="text-white font-bold mt-0.5">
            {activeVehicle ? formatVehicle(activeVehicle) : 'Tap to select a vehicle'}
          </Text>
        </View>
        <Text className="text-sky text-sm font-bold">{activeVehicle ? 'Change' : 'Select'} →</Text>
      </Pressable>

      {/* Search */}
      {!!activeVehicle && (
        <TextInput
          value={searchQuery}
          onChangeText={setSearchQuery}
          placeholder="Search guides… (Oil, Brakes, Battery)"
          placeholderTextColor="rgba(255,255,255,0.3)"
          className="bg-charcoal/40 border border-white/10 rounded-xl px-4 py-3 text-white mb-3"
        />
      )}

      {/* Tab type pills */}
      <View className="flex-row flex-wrap mb-1">
        {[
          { key: 'all', label: 'All Guides' },
          { key: 'maintenance', label: 'Maintenance' },
          { key: 'repair', label: 'Repair' },
          { key: 'road-trip-inspection', label: 'Road Trip' },
        ].map(({ key, label }) => (
          <FilterPill key={key} label={label} active={filterTabType === key}
            onPress={() => setFilterTabType(key)} variant="primary" />
        ))}
      </View>

      {/* Category pills (only when a vehicle is selected & there are categories) */}
      {!!activeVehicle && categoryList.length > 0 && (
        <View className="flex-row flex-wrap mb-1">
          <FilterPill label="All Categories" active={filterCategory === 'all'}
            onPress={() => setFilterCategory('all')} />
          {categoryList.map(cat => (
            <FilterPill key={cat} label={cat} active={filterCategory === cat}
              onPress={() => setFilterCategory(cat)} />
          ))}
        </View>
      )}

      {/* Difficulty pills */}
      <View className="flex-row flex-wrap items-center">
        <Text className="text-[10px] font-bold text-white/30 uppercase tracking-wider mr-2 mb-2">Level:</Text>
        {['all', 'beginner', 'intermediate', 'advanced'].map(level => (
          <FilterPill
            key={level}
            label={level === 'all' ? 'All' : level}
            active={filterDifficulty === level}
            onPress={() => setFilterDifficulty(level)}
          />
        ))}
        {!!activeVehicle && (
          <Text className="ml-auto text-xs text-white/30 mb-2">
            {filtered.length} guide{filtered.length !== 1 ? 's' : ''}
          </Text>
        )}
      </View>

      {!!activeVehicle && (
        <Text className="text-2xl font-black text-white mt-4 mb-1">
          Guides for your {activeVehicle.year} {activeVehicle.make} {activeVehicle.model}
        </Text>
      )}
    </View>
  );

  // Empty / no-vehicle states are rendered as the "data" content.
  if (!activeVehicle) {
    return (
      <FlatList
        className="flex-1 bg-navy"
        data={[]}
        keyExtractor={(_, i) => String(i)}
        renderItem={null}
        ListHeaderComponent={Header}
        ListEmptyComponent={
          <View className="mx-5 mt-4 items-center bg-charcoal/40 rounded-2xl border border-white/10 px-6 py-12">
            <IconWrench size={48} />
            <Text className="text-2xl font-black text-white mt-4 mb-2">Select a Vehicle</Text>
            <Text className="text-white/50 text-center mb-6">
              Add a vehicle to see guides specifically tailored to your car.
            </Text>
            <Pressable
              onPress={() => navigation.navigate('VehicleSelector')}
              className="bg-sky px-7 py-3 rounded-xl active:opacity-80"
            >
              <Text className="text-white font-bold">Choose Vehicle →</Text>
            </Pressable>
          </View>
        }
      />
    );
  }

  return (
    <FlatList
      className="flex-1 bg-navy"
      contentContainerStyle={{ paddingBottom: 32 }}
      data={filtered}
      keyExtractor={item => item.id}
      ListHeaderComponent={Header}
      renderItem={({ item }) => (
        <View className="px-5">
          <GuideCard
            guide={item}
            isMatch
            onPress={() => navigation.navigate('GuideDetail', { id: item.id, title: item.title })}
          />
        </View>
      )}
      ListEmptyComponent={
        <View className="mx-5 mt-4 items-center bg-charcoal/40 rounded-2xl border border-white/10 px-6 py-12">
          <IconWrench size={40} />
          {searchQuery ? (
            <>
              <Text className="text-xl font-black text-white mt-4 mb-2">No guides found</Text>
              <Text className="text-white/40 mb-4">No guides match "{searchQuery}"</Text>
              <Pressable
                onPress={() => setSearchQuery('')}
                className="bg-sky px-6 py-2.5 rounded-xl active:opacity-80"
              >
                <Text className="text-white font-bold text-sm">Clear Search</Text>
              </Pressable>
            </>
          ) : (
            <>
              <Text className="text-xl font-black text-white mt-4 mb-2">No guides yet</Text>
              <Text className="text-white/40 text-center">
                We're adding guides for your {formatVehicle(activeVehicle)} weekly.
              </Text>
            </>
          )}
        </View>
      }
      ListFooterComponent={
        <View className="mx-5 mt-8 pt-6 border-t border-white/10">
          <Text className="text-xs text-white/40 text-center">
            <Text className="font-semibold text-white/60">⚠️ Important: </Text>
            These guides are created through independent research and are NOT OEM documentation.
            Always verify with your vehicle's official manual.
          </Text>
        </View>
      }
    />
  );
}
